/** Small browser PDF renderer for post-install documents. Each A4 page is
 * drawn to a canvas, then embedded as a JPEG page. This keeps the output
 * bilingual, preserves photo orientation and needs no provider or PDF service.
 * The generated bytes are attached to a saved communication draft; sending
 * still uses the normal human review. */
export interface CompletionLine {scope:string}
/** Only reviewed completed scope enters the customer report. Free-text office
 * confirmation and installer notes remain private operational evidence. */
/** A crew day as the Job records it: its scope description, and whether it was closed by the office or
 *  reported done. Jacky, 8 October 2026, on J-0926-10228's report showing one product line: *"it used to be
 *  all the task action, this job including skirting and door arch"* — that Job has no installer day
 *  reports (the crew's saves failed on 1 October) but every crew day carries its scope and was closed by
 *  the office. The report lists the scope of each such day; the product name is the last resort only. */
export interface CompletionCrewDay {id?:string|null;date:string;task?:string|null;done?:string|null;office_state?:{state?:string}|null}
/** `installComplete`: the installation record itself reads Install Complete (the platform's gate). Jacky, 13 October
 *  2026, J-0926-10222: the Job was finished on the platform, no crew day was closed in the CRM and no installer report
 *  was saved, so the report could not be built at all. A finished installation's crew days are the work that was
 *  carried out: each day's scope is listed. */
export function completionScopeLines(reports:Array<{kind:string;checklist:Record<string,boolean>;work_order_complete?:boolean;scope_description:string|null;reported_at:string;notes?:string}>,officeConfirmed:boolean,fallbackProduct:string|null,crewDays:CompletionCrewDay[]=[],installComplete=false):CompletionLine[]{
  const seen=new Set<string>();const lines:CompletionLine[]=[];
  const add=(scope:string)=>{const key=scope.trim().replace(/\s+/g,' ');if(key&&!seen.has(key.toLowerCase())){seen.add(key.toLowerCase());lines.push({scope:key});}};
  for(const r of reports.filter(r=>r.kind==='completion'&&r.checklist?.scope_completed===true&&r.scope_description?.trim()).sort((a,b)=>a.reported_at.localeCompare(b.reported_at)))add(r.scope_description!);
  // Crew days the office closed or the crew marked done: their scope is the work that was carried out.
  for(const d of [...crewDays].filter(d=>d.id&&d.task?.trim()&&(d.office_state?.state==='closed'||!!d.done||installComplete)).sort((a,b)=>a.date.localeCompare(b.date)))add(d.task!);
  if(!lines.length&&(officeConfirmed||installComplete)&&fallbackProduct?.trim())add(fallbackProduct);
  return lines;
}
type CompletionBasis='actual'|'office'|'installer'|'scheduled';
export interface CompletionPdfInput {jobRef:string;customer:string;address:string;completedOn:string;completionBasis:CompletionBasis;issuedOn:string;lines:CompletionLine[];photos:Blob[]}
/** `manufacturer.documents` are the supplier warranty PDFs on file for the product, attached with the
 *  statement. When the catalogue holds none the statement says so and points to the manufacturer's
 *  published terms — it is never blocked on a file a person would have to go and find. */
export interface WarrantyPdfInput {jobRef:string;customer:string;address:string;completedOn:string;completionBasis:CompletionBasis;expiresOn:string;issuedOn:string;product:string;manufacturer:{supplier:string|null;documents:string[]}}
function completionLabel(basis:CompletionBasis){return basis==='actual'?'Installation completed':basis==='office'?'Office confirmed completion':basis==='scheduled'?'Installation date':'Installer reported completion'}

const W=595,H=842,SCALE=2;
type Sheet={canvas:HTMLCanvasElement;ctx:CanvasRenderingContext2D};
function sheet(title:string,ref:string):Sheet{
  const canvas=document.createElement('canvas');canvas.width=W*SCALE;canvas.height=H*SCALE;
  const ctx=canvas.getContext('2d');if(!ctx)throw Error('PDF preview is unavailable on this browser');
  ctx.scale(SCALE,SCALE);ctx.fillStyle='#fff';ctx.fillRect(0,0,W,H);
  ctx.fillStyle='#222';ctx.font='bold 18px Arial, sans-serif';ctx.fillText('ARTEMIS',42,58);
  ctx.fillStyle='#6b6b6b';ctx.font='8px Arial, sans-serif';ctx.fillText('FLOORING',43,71);
  ctx.fillStyle='#222';ctx.font='16px Arial, sans-serif';ctx.textAlign='right';ctx.fillText(title,W-42,58);ctx.textAlign='left';
  ctx.fillStyle='#777';ctx.font='9px Arial, sans-serif';ctx.textAlign='right';ctx.fillText(ref,W-42,73);ctx.textAlign='left';
  ctx.strokeStyle='#222';ctx.lineWidth=1.5;ctx.beginPath();ctx.moveTo(42,85);ctx.lineTo(W-42,85);ctx.stroke();
  ctx.strokeStyle='#ddd';ctx.lineWidth=0.5;ctx.beginPath();ctx.moveTo(42,H-35);ctx.lineTo(W-42,H-35);ctx.stroke();
  ctx.fillStyle='#777';ctx.font='8px Arial, sans-serif';ctx.fillText('Artemis Flooring · 02 8880 9861 · sales@artemisflooring.com',42,H-23);
  return {canvas,ctx};
}
function line(ctx:CanvasRenderingContext2D,label:string,value:string,y:number):number{
  ctx.fillStyle='#777';ctx.font='8px Arial, sans-serif';ctx.fillText(label.toUpperCase(),42,y);
  ctx.fillStyle='#222';ctx.font='11px Arial, sans-serif';return paragraph(ctx,value||'—',42,y+17,W-84,14)+11;
}
function section(ctx:CanvasRenderingContext2D,label:string,y:number):number{
  ctx.fillStyle='#aa8549';ctx.font='bold 10px Arial, sans-serif';ctx.fillText(label.toUpperCase(),42,y);
  ctx.strokeStyle='#ddd';ctx.lineWidth=0.5;ctx.beginPath();ctx.moveTo(42,y+7);ctx.lineTo(W-42,y+7);ctx.stroke();return y+23;
}
function paragraph(ctx:CanvasRenderingContext2D,value:string,x:number,y:number,width:number,step=15):number{
  for(const raw of value.split('\n')){
    if(!raw.trim()){y+=step;continue;}
    let row='';
    for(const word of raw.split(/\s+/)){
      const next=row?`${row} ${word}`:word;
      if(row&&ctx.measureText(next).width>width){ctx.fillText(row,x,y);y+=step;row=word;}else row=next;
    }
    if(row){ctx.fillText(row,x,y);y+=step;}
  }
  return y;
}
function keepRoom(pages:Sheet[],title:string,ref:string,y:number,need:number):{page:Sheet;y:number}{
  if(y+need<H-70)return {page:pages.at(-1)!,y};
  const page=sheet(title,ref);pages.push(page);return {page,y:115};
}
async function imageFromBlob(blob:Blob):Promise<HTMLImageElement>{
  const url=URL.createObjectURL(blob);
  try{return await new Promise((resolve,reject)=>{const img=new Image();img.onload=()=>resolve(img);img.onerror=()=>reject(Error('A selected completion photo could not be decoded'));img.src=url;});}
  finally{setTimeout(()=>URL.revokeObjectURL(url),60_000);}
}
function drawContained(ctx:CanvasRenderingContext2D,img:HTMLImageElement,x:number,y:number,w:number,h:number){
  const ratio=Math.min(w/img.naturalWidth,h/img.naturalHeight),dw=img.naturalWidth*ratio,dh=img.naturalHeight*ratio;
  ctx.drawImage(img,x+(w-dw)/2,y+(h-dh)/2,dw,dh);
}
export async function completionPdf(input:CompletionPdfInput):Promise<Uint8Array>{
  if(!input.lines.length)throw Error('A saved installer day report is required');
  const title='Job Completion Report',pages=[sheet(title,input.jobRef)];let page=pages[0]!,ctx=page.ctx,y=113;
  y=line(ctx,'Customer',input.customer,y);y=line(ctx,'Site address',input.address,y);y=line(ctx,'Job reference',input.jobRef,y);
  y=line(ctx,completionLabel(input.completionBasis),input.completedOn,y);y=line(ctx,'Issued',input.issuedOn,y);
  y=section(ctx,'Completed scope of work',y+12);
  for(const report of input.lines){
    ({page,y}=keepRoom(pages,title,input.jobRef,y,90));ctx=page.ctx;
    ctx.strokeStyle='#28704b';ctx.lineWidth=1.5;ctx.strokeRect(42,y-10,13,13);
    ctx.beginPath();ctx.moveTo(45,y-4);ctx.lineTo(49,y);ctx.lineTo(53,y-7);ctx.stroke();
    ctx.fillStyle='#222';ctx.font='10px Arial, sans-serif';
    y=paragraph(ctx,report.scope,66,y,W-108,14)+18;
  }
  if(input.photos.length){
    for(let first=0;first<input.photos.length;first+=4){
      page=sheet('Completion Photos',input.jobRef);pages.push(page);ctx=page.ctx;
      for(let index=first;index<Math.min(first+4,input.photos.length);index++){
        const img=await imageFromBlob(input.photos[index]!);
        const pos=index-first,x=42+(pos%2)*260,top=115+Math.floor(pos/2)*325;
        ctx.fillStyle='#f5f3ef';ctx.fillRect(x,top,250,295);
        drawContained(ctx,img,x+5,top+5,240,270);
        ctx.fillStyle='#555';ctx.font='9px Arial, sans-serif';ctx.fillText(`Photo ${index+1} of ${input.photos.length}`,x+5,top+287);
      }
    }
  }
  return encodePdf(pages);
}
export async function warrantyPdf(input:WarrantyPdfInput):Promise<Uint8Array>{
  if(!input.completedOn||!input.product)throw Error('Completion date and supplied product are required');
  const title='Warranty Statement',pages=[sheet(title,input.jobRef)],ctx=pages[0]!.ctx;let y=113;
  y=line(ctx,'Issued to',input.customer,y);y=line(ctx,'Site address',input.address,y);y=line(ctx,'Job reference',input.jobRef,y);
  y=section(ctx,'Workmanship warranty',y+12);
  ctx.fillStyle='#333';ctx.font='10px Arial, sans-serif';
  y=paragraph(ctx,'Artemis Flooring warrants the craftsmanship of flooring installed under this job for 12 months from the completion date recorded below. This covers defects in workmanship arising from installation by Artemis Flooring. It does not cover misuse, accident, normal wear and tear, moisture, subfloor movement, or alterations by others.',42,y,W-84,16)+15;
  y=line(ctx,completionLabel(input.completionBasis),input.completedOn,y);y=line(ctx,'Workmanship warranty expires',input.expiresOn,y);
  y=section(ctx,'Manufacturer warranty',y+12);
  ctx.fillStyle='#333';ctx.font='10px Arial, sans-serif';
  y=paragraph(ctx,'The following supplied product is subject to the manufacturer warranty and its own terms and conditions:',42,y,W-84,16)+8;
  ctx.fillStyle='#222';ctx.font='bold 11px Arial, sans-serif';y=paragraph(ctx,input.product,50,y,W-100,17)+8;
  ctx.fillStyle='#777';ctx.font='10px Arial, sans-serif';
  if(input.manufacturer.documents.length){
    y=paragraph(ctx,'Manufacturer warranty coverage and exclusions are stated in the following supplier document(s), attached with this statement:',50,y,W-100,15)+8;
    for(const name of input.manufacturer.documents)y=paragraph(ctx,name,50,y,W-100,15)+3;
  }else{
    y=paragraph(ctx,`Manufacturer warranty coverage and exclusions are as published by ${input.manufacturer.supplier||'the manufacturer'} for this product. Artemis Flooring will provide a copy of the manufacturer's warranty terms on request.`,50,y,W-100,15)+3;
  }
  y+=20;
  ctx.fillStyle='#222';ctx.font='12px Arial, sans-serif';ctx.fillText('Artemis Flooring',42,y);y+=18;
  ctx.fillStyle='#777';ctx.font='9px Arial, sans-serif';ctx.fillText(`Authorised representative · Issued ${input.issuedOn}`,42,y);
  return encodePdf(pages);
}

function ascii(s:string):Uint8Array{return new TextEncoder().encode(s)}
function join(parts:Uint8Array[]):Uint8Array{const size=parts.reduce((n,p)=>n+p.length,0),out=new Uint8Array(size);let at=0;for(const p of parts){out.set(p,at);at+=p.length;}return out}
function jpeg(canvas:HTMLCanvasElement,quality:number):Uint8Array{
  const data=canvas.toDataURL('image/jpeg',quality).split(',')[1];if(!data)throw Error('PDF page could not be rendered');
  const binary=atob(data),out=new Uint8Array(binary.length);for(let i=0;i<binary.length;i++)out[i]=binary.charCodeAt(i);return out;
}
/** Embed rasterised A4 pages into a standards-conforming PDF 1.4 file. */
export function pdfBytes(pages:Sheet[],quality:number):Uint8Array{
  const images=pages.map(p=>jpeg(p.canvas,quality)),parts:Uint8Array[]=[ascii('%PDF-1.4\n%ARTEMIS\n')],offsets=[0];
  let length=parts[0]!.length;
  const add=(part:Uint8Array)=>{parts.push(part);length+=part.length};
  const object=(id:number,body:Uint8Array)=>{offsets[id]=length;add(ascii(`${id} 0 obj\n`));add(body);add(ascii('\nendobj\n'));};
  const pageIds=pages.map((_,i)=>3+i*3);
  object(1,ascii('<< /Type /Catalog /Pages 2 0 R >>'));
  object(2,ascii(`<< /Type /Pages /Kids [${pageIds.map(i=>`${i} 0 R`).join(' ')}] /Count ${pages.length} >>`));
  for(let i=0;i<pages.length;i++){
    const id=pageIds[i]!,imgId=id+2,contentId=id+1,im=images[i]!;
    object(id,ascii(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${W} ${H}] /Resources << /XObject << /Im${i} ${imgId} 0 R >> >> /Contents ${contentId} 0 R >>`));
    const drawing=ascii(`q ${W} 0 0 ${H} 0 0 cm /Im${i} Do Q\n`);
    object(contentId,join([ascii(`<< /Length ${drawing.length} >>\nstream\n`),drawing,ascii('endstream')]));
    object(imgId,join([ascii(`<< /Type /XObject /Subtype /Image /Width ${pages[i]!.canvas.width} /Height ${pages[i]!.canvas.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${im.length} >>\nstream\n`),im,ascii('\nendstream')]));
  }
  const xref=length,count=2+pages.length*3;
  add(ascii(`xref\n0 ${count+1}\n0000000000 65535 f \n`));
  for(let id=1;id<=count;id++)add(ascii(`${String(offsets[id]).padStart(10,'0')} 00000 n \n`));
  add(ascii(`trailer\n<< /Size ${count+1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`));
  return join(parts);
}
function encodePdf(pages:Sheet[]):Uint8Array{
  for(const quality of [0.82,0.7,0.58,0.46,0.34]){const pdf=pdfBytes(pages,quality);if(pdf.length<1_950_000)return pdf;}
  throw Error('The report is too large for one communication attachment. Review the photo count before issuing it.');
}
